import React, { useState, useEffect } from 'react';
import { useI18n } from '../i18n';
import { usePermissions } from '../contexts/PermissionContext';
import { useAccount } from '../contexts/AccountContext';
import { userApi } from '../api';
import { showToast, showConfirm } from '../utils/toast';

export default function UserManagement({ user: currentUser, subStatus, onSubscribe }) {
  const { t } = useI18n();
  const { hasPermission } = usePermissions();
  const { selectedAccountId } = useAccount();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('BILLING');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  // Available roles for assignment
  const availableRoles = [
    { key: 'ORG_ADMIN', name: 'Organization Admin', description: 'Full access to all features' },
    { key: 'BILLING', name: 'Billing', description: 'Create orders, manage products, customers, and handle expenses' },
    { key: 'VIEWER', name: 'Viewer', description: 'View orders, expenses, and reports only' },
  ];

  useEffect(() => {
    loadUsers();
    loadAccounts();
  }, [selectedAccountId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getOrgUsers();
      setUsers(response);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const { accountsApi } = await import('../api');
      const response = await accountsApi.listOrgAccounts();
      setAccounts(response);
      // Set default account if none selected
      if (response.length > 0 && !selectedAccount) {
        setSelectedAccount(response[0].accountId);
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const handleInviteUser = async () => {
    if (!invitePhone.trim() || !inviteName.trim() || !selectedAccount) return;
    
    // Validate email uniqueness if provided
    if (inviteEmail.trim()) {
      // Check against all users in current organization first
      const existingUser = users.find(user => 
        user.email && user.email.toLowerCase() === inviteEmail.toLowerCase()
      );
      if (existingUser) {
        showToast({
          message: 'A user with this email address already exists in your organization',
          variant: 'warning',
        });
        return;
      }
      
      // Note: Backend will also validate email uniqueness across the entire system
      // and return appropriate error message if email exists elsewhere
    }
    
    try {
      setIsSaving(true);
      await userApi.createUser({
        name: inviteName,
        email: inviteEmail || undefined, // Only include if provided
        phone: invitePhone,
        password: invitePassword,
        accountRoles: [{
          accountId: selectedAccount,
          role: selectedRole
        }]
      });
      
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      setInvitePhone('');
      setInvitePassword('');
      setSelectedRole('BILLING');
      setSelectedAccount('');
      await loadUsers();
    } catch (error) {
      console.error('Failed to invite user:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create user';
      showToast({ message: errorMessage, variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setInviteName(user.name);
    setInviteEmail(user.email);
    setInvitePhone(user.phone || '');
    setInvitePassword(''); // Don't pre-fill password
    setSelectedRole(user.roles?.[0]?.role || 'BILLING');
    setSelectedAccount(user.roles?.[0]?.accountId || '');
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !invitePhone.trim() || !inviteName.trim() || !selectedAccount) return;
    
    // Validate email uniqueness if provided and different from current
    if (inviteEmail.trim() && inviteEmail.toLowerCase() !== selectedUser.email?.toLowerCase()) {
      // Check against all users in current organization first
      const existingUser = users.find(user => 
        user.userId !== selectedUser.userId && 
        user.email && 
        user.email.toLowerCase() === inviteEmail.toLowerCase()
      );
      if (existingUser) {
        showToast({
          message: 'A user with this email address already exists in your organization',
          variant: 'warning',
        });
        return;
      }
      
      // Note: Backend will also validate email uniqueness across the entire system
      // and return appropriate error message if email exists elsewhere
    }
    
    try {
      setIsSaving(true);
      await userApi.updateUser(selectedUser.userId, {
        name: inviteName,
        email: inviteEmail || undefined, // Only include if provided
        phone: invitePhone,
        password: invitePassword || undefined, // Only include if provided
        accountRoles: [{
          accountId: selectedAccount,
          role: selectedRole
        }]
      });
      
      setShowEditModal(false);
      setSelectedUser(null);
      setInviteEmail('');
      setInviteName('');
      setInvitePhone('');
      setInvitePassword('');
      setSelectedRole('BILLING');
      setSelectedAccount('');
      await loadUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update user';
      showToast({ message: errorMessage, variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      setIsSaving(true);
      await userApi.updateUser(userId, {
        role: newRole,
        accountId: selectedAccountId
      });
      
      await loadUsers();
    } catch (error) {
      console.error('Failed to change role:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    const confirmed = await showConfirm({
      title: 'Remove user?',
      message: 'Are you sure you want to remove this user?',
      confirmLabel: 'Remove',
      cancelLabel: 'Cancel',
      destructive: true,
      variant: 'warning',
    });
    if (!confirmed) return;
    
    try {
      setIsSaving(true);
      await userApi.deleteUser(userId);
      
      await loadUsers();
    } catch (error) {
      console.error('Failed to remove user:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getRoleInfo = (roleKey) => {
    return availableRoles.find(role => role.key === roleKey) || { name: roleKey, description: 'Unknown role' };
  };

  const formatLastActive = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Check if user has permission to manage users
  if (!hasPermission('users:manage')) {
    return (
      <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
        <div className="text-center py-8">
          <div className="text-gray-500 mb-4">🔒</div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to manage users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <span className="text-2xl">👥</span>
                User Management
              </h1>
              <p className="text-gray-600 mt-1">Manage team members and their access permissions</p>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 shadow-sm"
              disabled={isSaving}
            >
              <span className="text-lg">+</span>
              Add User
            </button>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Team Members</h2>
              <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                {users.length} {users.length === 1 ? 'member' : 'members'}
              </span>
            </div>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="text-gray-500 mt-4">Loading team members...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
              <p className="text-gray-500 mb-6">Invite your first team member to get started!</p>
              <button
                onClick={() => setShowInviteModal(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Invite First Member
              </button>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {users.map((user) => {
                  const primaryRole = user.roles?.[0]?.role || 'VIEWER';
                  const roleInfo = getRoleInfo(primaryRole);
                  return (
                    <div key={user.userId} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-green-700 font-bold text-lg">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium text-gray-900 truncate">{user.name}</h3>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                primaryRole === 'ORG_ADMIN' ? 'bg-purple-100 text-purple-800' :
                                primaryRole === 'BILLING' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {roleInfo.name}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 truncate mt-1">{user.email}</p>
                          {user.phone && (
                            <p className="text-xs text-gray-400 mt-1">{user.phone}</p>
                          )}
                          
                          {/* Role Management */}
                          <div className="mt-3 space-y-2">
                            {user.userId === currentUser.userId && (
                              <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                🔒 You cannot edit your own permissions
                              </div>
                            )}
                            <select
                              value={primaryRole}
                              onChange={(e) => handleChangeRole(user.userId, e.target.value)}
                              className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                              disabled={isSaving || user.userId === currentUser.userId}
                            >
                              {availableRoles.map(role => (
                                <option key={role.key} value={role.key}>
                                  {role.name}
                                </option>
                              ))}
                            </select>
                            
                            {user.roles && user.roles.length > 1 && (
                              <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                +{user.roles.length - 1} more account{user.roles.length - 1 > 1 ? 's' : ''}
                              </div>
                            )}
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditUser(user)}
                                className={`flex-1 text-sm px-3 py-2 rounded-md transition-colors ${
                                  user.userId === currentUser.userId
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                                disabled={isSaving || user.userId === currentUser.userId}
                                title={user.userId === currentUser.userId ? 'You cannot edit your own account' : 'Edit user'}
                              >
                                {user.userId === currentUser.userId ? 'Self' : 'Edit'}
                              </button>
                              {user.userId !== currentUser.userId && (
                                <button
                                  onClick={() => handleRemoveUser(user.userId)}
                                  className="flex-1 text-red-600 hover:text-red-800 text-sm px-3 py-2 rounded-md hover:bg-red-50 transition-colors"
                                  disabled={isSaving}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Invite User Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <span className="text-2xl">👤</span>
                    Add Team Member
                  </h3>
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
              
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="user@company.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={invitePhone}
                      onChange={(e) => setInvitePhone(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="+1 (555) 123-4567"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={invitePassword}
                      onChange={(e) => setInvitePassword(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Create a secure password"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Account *
                    </label>
                    <select
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      required
                    >
                      <option value="">Select an account</option>
                      {accounts.map(account => (
                        <option key={account.accountId} value={account.accountId}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose which business account this user will have access to
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role & Permissions
                    </label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    >
                      {availableRoles.map(role => (
                        <option key={role.key} value={role.key}>
                          {role.name} - {role.description}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose the appropriate access level for this team member
                    </p>
                  </div>
                </div>
              
                <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 px-6 py-3 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInviteUser}
                    className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving || !invitePhone.trim() || !inviteName.trim() || !selectedAccount}
                  >
                    {isSaving ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Adding Member...
                      </span>
                    ) : (
                      'Add Team Member'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <span className="text-2xl">✏️</span>
                    Edit Team Member
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
              
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="user@company.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={invitePhone}
                      onChange={(e) => setInvitePhone(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="+1 (555) 123-4567"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password (leave blank to keep current)
                    </label>
                    <input
                      type="password"
                      value={invitePassword}
                      onChange={(e) => setInvitePassword(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Enter new password (optional)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Account *
                    </label>
                    <select
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      required
                    >
                      <option value="">Select an account</option>
                      {accounts.map(account => (
                        <option key={account.accountId} value={account.accountId}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role & Permissions
                    </label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                    >
                      {availableRoles.map(role => (
                        <option key={role.key} value={role.key}>
                          {role.name} - {role.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              
                <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-6 py-3 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateUser}
                    className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving || !invitePhone.trim() || !inviteName.trim() || !selectedAccount}
                  >
                    {isSaving ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Updating...
                      </span>
                    ) : (
                      'Update User'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

